export interface ToolResultSummary {
	detail?: string;
	summary?: string;
}

export interface SummarizeToolResultOptions {
	commit?: boolean;
}

const FAILED_PREFIX = /^\s*Failed:\s*/i;
const MAX_SUMMARY = 600;
const MAX_DETAIL = 2000;
const MAX_NAME = 60;
const MAX_NAMES = 3;

const GUID_VALUE = /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{32})$/i;
const CONTAINS_GUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\b[0-9a-f]{32}\b/i;
const UNTRUSTED_TAG = /<\/?untrusted_tool_output>/gi;
const UNTRUSTED_PRESENT = /<\/?untrusted_tool_output>/i;
const QUOTED_KEY = /"[^"]+"\s*:/;
const TOOL_ID = /\b(?:wsapi|fernie)[-_][\w-]+(?![\w@-])/;
const SENTENCE = /[\s\S]*?(?:[.!?]+(?=\s|$)|$)/g;

const NAME_KEYS = [
	"name",
	"customername",
	"displayname",
	"fullname",
	"title",
	"label",
	"ordernumber",
	"order_number",
	"number",
	"reference",
	"code",
	"sku",
	"itemname",
	"productname",
	"description",
	"subject",
	"email",
	"username"
];

const MESSAGE_KEYS = ["error", "message", "reason", "detail", "statusmessage"];

const GENERIC_COLLECTION_KEYS = new Set(["data", "content", "payload", "result", "results", "response", "output"]);

const clamp = (value: string, max: number): string => (value.length > max ? `${value.slice(0, max)}…` : value);

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

const stripWrapper = (raw: string): string =>
	raw
		.trim()
		.replace(/^<untrusted_tool_output>/i, "")
		.replace(/<\/untrusted_tool_output>$/i, "")
		.replace(/^untrusted_tool_output\b/i, "")
		.trim();

const tryParse = (value: string): unknown => {
	try {
		return JSON.parse(value);
	} catch {
		return undefined;
	}
};

const lowerKeyed = (record: Record<string, unknown>): Map<string, unknown> => {
	const map = new Map<string, unknown>();
	for (const [key, value] of Object.entries(record)) map.set(key.toLowerCase(), value);
	return map;
};

const cleanName = (value: unknown): string | undefined => {
	if (typeof value !== "string") return undefined;
	const trimmed = value.trim();
	if (!trimmed || GUID_VALUE.test(trimmed) || CONTAINS_GUID.test(trimmed)) return undefined;
	return clamp(trimmed, MAX_NAME);
};

const pickName = (item: unknown): string | undefined => {
	if (!isRecord(item)) return undefined;
	const fields = lowerKeyed(item);
	for (const key of NAME_KEYS) {
		const name = cleanName(fields.get(key));
		if (name) return name;
	}
	return undefined;
};

const pickMessage = (record: Record<string, unknown>): string | undefined => {
	const fields = lowerKeyed(record);
	for (const key of MESSAGE_KEYS) {
		const value = fields.get(key);
		if (typeof value === "string" && value.trim() && !CONTAINS_GUID.test(value))
			return clamp(value.trim(), MAX_SUMMARY);
	}
	return undefined;
};

const humanizeNoun = (key: string): string =>
	key
		.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
		.replace(/[_-]+/g, " ")
		.trim()
		.toLowerCase();

const singularize = (noun: string): string => {
	if (/ies$/.test(noun)) return noun.replace(/ies$/, "y");
	if (/(ses|xes|zes|ches|shes)$/.test(noun)) return noun.replace(/es$/, "");
	if (/ss$/.test(noun)) return noun;
	if (/s$/.test(noun)) return noun.replace(/s$/, "");
	return noun;
};

const pluralize = (noun: string): string => {
	if (/[^aeiou]y$/.test(noun)) return noun.replace(/y$/, "ies");
	if (/(s|x|z|ch|sh)$/.test(noun)) return `${noun}es`;
	return `${noun}s`;
};

const nounForCount = (noun: string, count: number): string => {
	const base = singularize(noun);
	return count === 1 ? base : pluralize(base);
};

const nounFromKey = (key?: string): string =>
	key && !GENERIC_COLLECTION_KEYS.has(key.toLowerCase()) ? humanizeNoun(key) : "result";

const describeCollection = (items: unknown[], key?: string): string => {
	const noun = nounFromKey(key);
	if (items.length === 0) return `No ${pluralize(singularize(noun))} found`;
	const countLabel = `${items.length} ${nounForCount(noun, items.length)}`;
	const names: string[] = [];
	for (const item of items) {
		if (names.length >= MAX_NAMES) break;
		const name = pickName(item);
		if (name) names.push(name);
	}
	if (names.length === 0) return `Found ${countLabel}`;
	const remaining = items.length - names.length;
	const more = remaining > 0 ? `, +${remaining} more` : "";
	return `Found ${countLabel}: ${names.join(", ")}${more}`;
};

const findPrimaryCollection = (record: Record<string, unknown>): { items: unknown[]; key: string } | undefined => {
	let primary: { items: unknown[]; key: string } | undefined;
	for (const [key, value] of Object.entries(record)) {
		if (!Array.isArray(value)) continue;
		if (!primary || value.length > primary.items.length) primary = { items: value, key };
	}
	return primary;
};

const summarizeRecord = (record: Record<string, unknown>): string | undefined => {
	const collection = findPrimaryCollection(record);
	if (collection) return describeCollection(collection.items, collection.key);
	const message = pickMessage(record);
	if (message) return message;
	const name = pickName(record);
	if (name) return `Found ${name}`;
	return undefined;
};

const pickErrorsMessage = (record: Record<string, unknown>): string | undefined => {
	const errors = lowerKeyed(record).get("errors");
	if (!Array.isArray(errors)) return undefined;
	for (const entry of errors) {
		if (typeof entry === "string" && entry.trim() && !CONTAINS_GUID.test(entry))
			return clamp(entry.trim(), MAX_SUMMARY);
		const message = isRecord(entry) ? pickMessage(entry) : undefined;
		if (message) return message;
	}
	return undefined;
};

const looksMachine = (value: string): boolean =>
	CONTAINS_GUID.test(value) || UNTRUSTED_PRESENT.test(value) || QUOTED_KEY.test(value);

const summarizeCommitJson = (value: unknown): string | undefined => {
	const message = isRecord(value) ? (pickMessage(value) ?? pickErrorsMessage(value)) : undefined;
	return message && !looksMachine(message) ? message : undefined;
};

const summarizeJson = (value: unknown): string | undefined => {
	if (Array.isArray(value)) return describeCollection(value);
	if (isRecord(value)) return summarizeRecord(value);
	return undefined;
};

const sanitizeSummary = (summary: string | undefined): string | undefined => {
	if (!summary) return undefined;
	const cleaned = summary.replace(UNTRUSTED_TAG, "").trim();
	if (!cleaned || CONTAINS_GUID.test(cleaned)) return undefined;
	return clamp(cleaned, MAX_SUMMARY);
};

const buildDetail = (parsed: unknown, fallback: string): string => {
	try {
		return clamp(JSON.stringify(parsed, null, 2), MAX_DETAIL);
	} catch {
		return clamp(fallback, MAX_DETAIL);
	}
};

const summarizeProse = (value: string): ToolResultSummary => {
	const text = value.trim();
	if (!text) return {};
	if (looksMachine(text)) return { detail: clamp(text, MAX_DETAIL) };
	return { summary: clamp(text, MAX_SUMMARY) };
};

const isTextPart = (part: unknown): part is { text: string } =>
	isRecord(part) && part.type === "text" && typeof part.text === "string";

const envelopeText = (value: unknown): string | undefined => {
	if (!isRecord(value) || !Array.isArray(value.content) || value.content.length === 0) return undefined;
	return value.content.every(isTextPart) ? value.content.map((part) => part.text).join("\n") : undefined;
};

const summarizeInner = (inner: string, commit: boolean): ToolResultSummary => {
	const parsed = tryParse(inner);
	if (parsed === undefined) return summarizeProse(inner);
	if (typeof parsed === "string") return summarizeProse(parsed);
	const text = envelopeText(parsed);
	if (text !== undefined) return summarizeInner(text, commit);
	if (Array.isArray(parsed) || isRecord(parsed)) {
		const summary = commit ? summarizeCommitJson(parsed) : summarizeJson(parsed);
		return { detail: buildDetail(parsed, inner), summary: sanitizeSummary(summary) };
	}
	return {};
};

const dropToolSentences = (summary?: string): string | undefined =>
	summary
		?.match(SENTENCE)
		?.filter((sentence) => !TOOL_ID.test(sentence))
		.join("")
		.trim() || undefined;

const summarizeToolResult = (result?: string, options: SummarizeToolResultOptions = {}): ToolResultSummary => {
	const commit = options.commit === true;
	const inner = stripWrapper(result ?? "");
	if (!inner) return {};
	const first = summarizeInner(inner, commit);
	const readable =
		!commit || first.summary || !FAILED_PREFIX.test(inner)
			? first
			: summarizeInner(inner.replace(FAILED_PREFIX, ""), commit);
	return { ...readable, summary: dropToolSentences(readable.summary) };
};

export default summarizeToolResult;
